import { useEffect, useState } from "react";
import { Plus, Search, Archive, Trash2, Images, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", url: "", folder: "Général", alt: "", caption: "", filename: "" };

export default function MediaAdminPage() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [slots, setSlots] = useState([]);
  const [bindTarget, setBindTarget] = useState(null);
  const [slotChoice, setSlotChoice] = useState("");
  const [mode, setMode] = useState("upload"); // upload | url
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => {
    const params = {};
    if (search) params.q = search;
    if (folder) params.folder = folder;
    api.get("/admin/media", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], folders: [] }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search, folder]);

  const loadSlots = () => api.get("/admin/page-slots").then((r) => setSlots(r.data)).catch(() => setSlots([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSlots(); }, []);

  const slotLabel = (ref) => (slots.find((s) => s.slot === ref) || {}).label || ref;

  const bindSubmit = async () => {
    try {
      await api.put(`/admin/page-slots/${slotChoice}`, { media_id: bindTarget.id });
      toast.success("Image associée — visible immédiatement sur la page publique");
      setBindTarget(null);
      load();
      loadSlots();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unbindSlot = async (slot) => {
    try {
      await api.delete(`/admin/page-slots/${slot}`);
      toast.success("Association retirée — la page repasse au visuel par défaut");
      load();
      loadSlots();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setMode("upload"); setFile(null); setPreview(null); setOpen(true); };
  const openReplace = (m) => { setEditing(m); setForm({ name: m.name, url: m.url, folder: m.folder, alt: m.alt || "", caption: m.caption || "", filename: (m.filename || "").replace(/\.webp$/, "") }); setOpen(true); };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return f ? URL.createObjectURL(f) : null; });
    if (f && !form.name) setForm({ ...form, name: f.name.replace(/\.[^.]+$/, "") });
  };

  const submit = async () => {
    setSending(true);
    try {
      if (editing) {
        const payload = { name: form.name, url: form.url, folder: form.folder, alt: form.alt || null, caption: form.caption || null };
        if (form.filename.trim()) payload.filename = form.filename.trim();
        await api.patch(`/admin/media/${editing.id}`, payload);
        toast.success("Média mis à jour — les contenus associés utilisent automatiquement le nouveau visuel");
      } else if (mode === "upload") {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", form.name);
        fd.append("folder", form.folder);
        if (form.alt) fd.append("alt", form.alt);
        if (form.caption) fd.append("caption", form.caption);
        await api.post("/admin/media/upload", fd);
        toast.success("Photo envoyée — disponible immédiatement dans la médiathèque");
      } else {
        await api.post("/admin/media", { ...form, alt: form.alt || null, caption: form.caption || null });
        toast.success("Média ajouté à la médiathèque");
      }
      setOpen(false);
      setFile(null);
      setPreview(null);
      load();
    } catch (e) {
      if (e.code === "ERR_NETWORK") toast.error("Problème de connexion — vérifiez votre réseau puis réessayez.");
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const archive = async (m) => {
    try { await api.post(`/admin/media/${m.id}/archive`); toast.success("Média archivé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (m) => {
    try { await api.delete(`/admin/media/${m.id}`); toast.success("Média supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="media-loading">Chargement…</div>;

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">11 bis — Médiathèque centrale</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Médiathèque centrale</h1>
          <p className="mt-1 text-sm text-muted-foreground">Réutilisable par produits, réalisations, blog, partenaires et pages publiques. Suppression bloquée si le média est utilisé.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="media-create">
          <Plus className="h-4 w-4" /> Ajouter un média
        </button>
      </div>

      <div className="card-ae mt-6">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input className="input-ae !pl-9" placeholder="Rechercher (nom, texte alternatif)…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="media-search" />
          </div>
          <select className="input-ae w-auto" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="media-folder-filter" aria-label="Dossier">
            <option value="">Tous les dossiers</option>
            {(data.folders || []).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {data.items.length === 0 ? (
          <div className="p-5"><EmptyState icon={Images} title="Médiathèque vide">Ajoutez les visuels Art Event — ils seront réutilisables partout sans duplication.</EmptyState></div>
        ) : (
          <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4 xl:grid-cols-5" data-testid="media-grid">
            {data.items.map((m) => (
              <div key={m.id} className="card-ae overflow-hidden" data-testid={`media-card-${m.id}`}>
                <img src={m.url} alt={m.alt || m.name} className="aspect-square w-full object-cover" loading="lazy" />
                <div className="p-3">
                  <p className="truncate text-sm font-semibold text-deepgreen" title={m.name}>{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.folder} · utilisée {m.usage_count}× {m.usage_count > 0 && <span title="Suppression bloquée">(protégée)</span>}</p>
                  {m.filename && <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground" title={m.filename} data-testid={`media-filename-${m.id}`}>{m.filename}</p>}
                  {(m.usages || []).filter((u) => u.module === "page").map((u) => (
                    <p key={u.ref} className="mt-1 flex items-center justify-between gap-1 rounded-lg bg-sage-light px-2 py-1 text-[10px] font-semibold text-deepgreen" data-testid={`media-usage-${u.ref}`}>
                      <span className="truncate" title={u.ref}>{slotLabel(u.ref)}</span>
                      <button type="button" onClick={() => unbindSlot(u.ref)} className="shrink-0 rounded-full p-0.5 hover:bg-white" title="Retirer l'association" data-testid={`media-unbind-${u.ref}`}><X className="h-3 w-3" /></button>
                    </p>
                  ))}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => { setBindTarget(m); setSlotChoice(""); }} className="inline-flex items-center gap-1 rounded-full border border-sage bg-sage-light px-2.5 py-1 text-[10px] font-bold text-deepgreen hover:bg-sage hover:text-white" data-testid={`media-bind-${m.id}`}><Link2 className="h-3 w-3" /> Associer</button>
                    <button type="button" onClick={() => openReplace(m)} className="rounded-full border border-border px-2.5 py-1 text-[10px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`media-replace-${m.id}`}>Remplacer</button>
                    <button type="button" onClick={() => archive(m)} className="rounded-full border border-border px-2.5 py-1 text-[10px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`media-archive-${m.id}`}><Archive className="h-3 w-3" /></button>
                    <button type="button" onClick={() => remove(m)} className="rounded-full border border-red-200 px-2.5 py-1 text-[10px] font-bold text-red-600 hover:bg-red-50" data-testid={`media-delete-${m.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="media-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Remplacer / modifier le média" : "Ajouter un média"}</DialogTitle>
            <DialogDescription>{editing ? "Le remplacement de l'URL met à jour tous les contenus associés sans les casser." : "Depuis votre appareil (ordinateur ou mobile) ou par URL — la photo est utilisable immédiatement sur le site."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div className="flex gap-2" role="tablist">
                <button type="button" onClick={() => setMode("upload")} data-testid="media-mode-upload"
                  className={mode === "upload" ? "btn-primary-ae !px-4 !py-2 !text-xs" : "btn-secondary-ae !px-4 !py-2 !text-xs"}>
                  Depuis mon appareil
                </button>
                <button type="button" onClick={() => setMode("url")} data-testid="media-mode-url"
                  className={mode === "url" ? "btn-primary-ae !px-4 !py-2 !text-xs" : "btn-secondary-ae !px-4 !py-2 !text-xs"}>
                  Par URL
                </button>
              </div>
            )}
            <div><label className="label-ae" htmlFor="m-name">Nom</label><input id="m-name" className="input-ae" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="media-name" /></div>
            {editing || mode === "url" ? (
              <div><label className="label-ae" htmlFor="m-url">URL du visuel</label><input id="m-url" className="input-ae" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="media-url" /></div>
            ) : (
              <div>
                <label className="label-ae" htmlFor="m-file">Photo (JPEG, PNG ou WebP — 10 Mo max)</label>
                <input id="m-file" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="input-ae" data-testid="media-file-input" />
                {preview && <img src={preview} alt="Aperçu de la photo sélectionnée" className="mt-3 max-h-44 rounded-lg border border-border object-contain" data-testid="media-preview" />}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="m-folder">Dossier</label><input id="m-folder" className="input-ae" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} data-testid="media-folder" /></div>
              <div><label className="label-ae" htmlFor="m-alt">Texte alternatif (ALT)</label><input id="m-alt" className="input-ae" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} data-testid="media-alt" /></div>
            </div>
            <div><label className="label-ae" htmlFor="m-caption">Légende</label><input id="m-caption" className="input-ae" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="media-caption" /></div>
            {editing && (
              <div>
                <label className="label-ae" htmlFor="m-filename">Nom de fichier (SEO)</label>
                <input id="m-filename" className="input-ae font-mono text-xs" placeholder="arche-mariage-champetre" value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })} data-testid="media-filename" />
                <p className="mt-1 text-[11px] text-muted-foreground">Slugifié automatiquement, extension .webp ajoutée — améliore le référencement de l'image.</p>
              </div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={sending || form.name.length < 2 || (editing || mode === "url" ? form.url.length < 5 : !file)}
              className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50"
              data-testid="media-submit"
            >
              {sending ? "Envoi en cours…" : editing ? "Enregistrer" : mode === "upload" ? "Envoyer la photo" : "Ajouter à la médiathèque"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bindTarget} onOpenChange={() => setBindTarget(null)}>
        <DialogContent className="bg-cream" data-testid="media-bind-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">Associer à une page</DialogTitle>
            <DialogDescription>
              « {bindTarget?.name} » sera visible immédiatement sur l'emplacement choisi. Une seule image active par emplacement — associer remplace l'image actuelle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="label-ae" htmlFor="bind-slot">Emplacement de page</label>
              <select id="bind-slot" className="input-ae" value={slotChoice} onChange={(e) => setSlotChoice(e.target.value)} data-testid="media-bind-slot">
                <option value="">Choisir l'emplacement…</option>
                {slots.map((s) => (
                  <option key={s.slot} value={s.slot}>
                    {s.label}{s.media ? ` — actuellement : ${s.media.name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={bindSubmit} disabled={!slotChoice} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="media-bind-submit">
              Associer à cet emplacement
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
